// components/OrderStatusBadges.jsx
// ─────────────────────────────────────────────────────────────────────────────
// The one pill every admin order list uses for payment and delivery state.
//
// The dashboard mixes three UI kits — Material-UI `Chip` (restaurant + RW order
// tables), react-bootstrap `Badge` (healthcare), and a hand-rolled `<span>` pill
// (PaginatedOrderTable). Each had its own colours and its own idea of what
// "paid" meant, which is how the Orders list ended up saying PENDING for an
// order the details page called Paid. These components are framework-agnostic
// plain spans so every table can render the identical badge, and they take their
// colours and their paid/unpaid logic from utilities/paymentStatus.js.
//
// Use `<PaymentBadge order={order} />` — never re-derive payment state at the
// call site.
// ─────────────────────────────────────────────────────────────────────────────

import React from "react";
import {
  getPaymentState,
  getDeliveryCfg,
  PAYMENT_STATE_LABEL,
  PAYMENT_STATE_CFG,
} from "../utilities/paymentStatus";

const NEUTRAL = {
  bg: "#f0f2f5",
  color: "#6b7c93",
  border: "#e4e8ef",
  icon: "fa-circle",
};

/**
 * Generic pill. `cfg` is a {bg, color, border, icon} object.
 *
 * A PILL IS NOT A BUTTON. Reported 27 Sep 2026: "pills are not
 * distinguishable from buttons." They were not — this rendered a 1px solid
 * border, a tinted fill, 700 weight and a 20px radius, which is the exact
 * description of a small outline button, and it sat in table rows beside real
 * ones. Every status in the console looked pressable.
 *
 * The distinction is now carried by SHAPE, not colour, so it survives a
 * greyscale screenshot and a colour-blind reader: a pill is fully round with
 * no border and a flat tint; a button is a 6px rounded rectangle with a solid
 * fill, a hover, a focus ring and a pointer. `cursor: default` confirms it the
 * moment the pointer arrives. The shared rules live in
 * assets/css/lusuku-overrides.css under `.lk-pill`.
 *
 * `cfg.border` is still read, as a faint inner edge only where a tint is too
 * pale to find (the neutral grey), never as a full border.
 */
export const StatusBadge = ({ label, cfg, title, style, className }) => {
  const c = cfg || NEUTRAL;
  return (
    <span
      className={["lk-pill", className].filter(Boolean).join(" ")}
      title={title}
      style={{
        background: c.bg,
        color: c.color,
        ...style,
      }}
    >
      <i className={`fas ${c.icon}`} style={{ fontSize: 9 }} aria-hidden="true" />
      {label || "—"}
    </span>
  );
};

/**
 * Payment state for an order — PAID / COD / FAILED / PENDING.
 * Derived from the canonical fields (isPaid, paymentStatus, paymentResult), so
 * it always agrees with what the order-details page shows.
 */
export const PaymentBadge = ({ order, style }) => {
  const state = getPaymentState(order);
  return (
    <StatusBadge
      label={PAYMENT_STATE_LABEL[state]}
      cfg={PAYMENT_STATE_CFG[state]}
      title={
        order?.paymentMethod
          ? `Payment method: ${order.paymentMethod}`
          : undefined
      }
      style={style}
    />
  );
};

/**
 * Delivery state for an order. Covers the full delivery_status enum; unknown
 * values render neutral grey instead of masquerading as pending.
 */
export const DeliveryStatusBadge = ({ status, style }) => (
  <StatusBadge label={status || "—"} cfg={getDeliveryCfg(status)} style={style} />
);

/* A ticket order's own stage (server: utils/ticketOrderStage.js), by tone.
   Ticket orders have no delivery leg, so the delivery badge said
   NOT_APPLICABLE or FULFILLED about every one of them (16 Oct 2026). */
export const TICKET_STAGE_CFG = Object.freeze({
  success: { bg: "#dcfce7", color: "#166534", border: "#bbf7d0", icon: "fa-ticket-alt" },
  info: { bg: "#e0f2fe", color: "#075985", border: "#bae6fd", icon: "fa-sync-alt" },
  warning: { bg: "#fef3c7", color: "#92400e", border: "#fde68a", icon: "fa-hourglass-half" },
  danger: { bg: "#fee2e2", color: "#b91c1c", border: "#fecaca", icon: "fa-exclamation-circle" },
  neutral: { bg: "#f0f2f5", color: "#6b7c93", border: "#e4e8ef", icon: "fa-circle" },
});

/** `stage` is `{ key, label, tone, attention }` as the server sends it. */
export const TicketStageBadge = ({ stage, style }) =>
  stage ? (
    <StatusBadge
      label={stage.label}
      cfg={TICKET_STAGE_CFG[stage.tone] || TICKET_STAGE_CFG.neutral}
      title={stage.attention ? "Needs attention" : undefined}
      style={style}
    />
  ) : null;

export default PaymentBadge;
